import {NgFor, NgIf} from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatOptionModule} from '@angular/material/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {environment} from '@env/environment';
import {fuseAnimations} from '@fuse/animations';
import {AuthService} from '@shared/services/auth.service';
import {debounceTime, map, Subject, takeUntil} from 'rxjs';

type GlobalSearchSelection = { type: 'property' | 'tenant'; hit: any };

/**
 * Global navbar search: federated Algolia search across the `properties` and
 * `tenants` indices. Nothing is queried until the user types at least 2 chars.
 * A property result opens its detail page (/immobilien/:id); a tenant result
 * opens its own detail page (/mietersuche/:id).
 */
@Component({
    selector: 'global-search',
    templateUrl: './global-search.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'globalSearch',
    animations: fuseAnimations,
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatOptionModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
})
export class GlobalSearchComponent implements OnInit, OnDestroy {

    readonly minLength = 1;
    readonly debounce = 350;

    opened = false;
    loading = false;
    propertyHits: any[] = [];
    tenantHits: any[] = [];

    searchControl: FormControl = new FormControl('');

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private authService: AuthService,
        private router: Router,
        private cf: ChangeDetectorRef,
    ) {
    }

    /**
     * Focus the input as soon as the bar is expanded into the DOM.
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef) {
        if (value) {
            setTimeout(() => value.nativeElement.focus());
        }
    }

    ngOnInit(): void {
        this.searchControl.valueChanges
            .pipe(
                debounceTime(this.debounce),
                map((value: string) => (value || '').trim()),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((query: string) => {
                if (query.length < this.minLength) {
                    this.propertyHits = [];
                    this.tenantHits = [];
                    this.loading = false;
                    this.cf.markForCheck();
                    return;
                }
                this.runSearch(query);
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    open(): void {
        this.opened = true;
    }

    close(): void {
        this.opened = false;
        this.searchControl.setValue('');
        this.propertyHits = [];
        this.tenantHits = [];
        this.loading = false;
    }

    onKeydown(event: KeyboardEvent): void {
        if (event.code === 'Escape') {
            this.close();
        }
    }

    /**
     * Navigate on result selection, then collapse the bar.
     */
    onSelect(selection: GlobalSearchSelection): void {
        if (!selection?.hit) {
            return;
        }
        if (selection.type === 'property') {
            const id = selection.hit.objectID || selection.hit.id;
            if (id) {
                this.router.navigate(['/immobilien', id]);
            }
        } else if (selection.type === 'tenant') {
            const id = selection.hit.id || selection.hit.objectID;
            if (id) {
                this.router.navigate(['/mietersuche', id]);
            }
        }
        this.close();
    }

    // Prefer Algolia's highlighted value (contains <em> around matches),
    // fall back to the plain field. Result is bound via [innerHTML].
    private hl(highlighted: any, plain: any): string {
        if (highlighted && highlighted.value != null && highlighted.value !== '') {
            return highlighted.value;
        }
        return plain != null ? String(plain) : '';
    }

    propertyTitleHtml(p: any): string {
        const hr = p?._highlightResult;
        const name = this.hl(hr?.name, p?.name);
        if (name) {
            return name;
        }
        // h_id_text already contains the "H" prefix (e.g. "H10") and is what
        // matches when the user searches "H10".
        const hidText = this.hl(hr?.h_id_text, p?.h_id_text);
        if (hidText) {
            return hidText;
        }
        const hid = this.hl(hr?.h_id, p?.h_id);
        if (hid) {
            return 'H' + hid;
        }
        return [this.hl(hr?.address?.streetName, p?.address?.streetName), this.hl(hr?.address?.streetNumber, p?.address?.streetNumber)].filter(Boolean).join(' ') || '—';
    }

    propertySubtitleHtml(p: any): string {
        const hr = p?._highlightResult;
        const street = [this.hl(hr?.address?.streetName, p?.address?.streetName), this.hl(hr?.address?.streetNumber, p?.address?.streetNumber)].filter(Boolean).join(' ');
        const vicinity = this.hl(hr?.address?.vicinity, p?.address?.vicinity);
        return [street, vicinity].filter(Boolean).join(', ');
    }

    tenantTitleHtml(t: any): string {
        const hr = t?._highlightResult?.contactData;
        const cd = t?.contactData;
        const name = [this.hl(hr?.firstName, cd?.firstName), this.hl(hr?.lastName, cd?.lastName)].filter(Boolean).join(' ').trim();
        return this.hl(hr?.displayName, cd?.displayName) || name || this.hl(hr?.companyName, cd?.companyName) || '—';
    }

    tenantSubtitleHtml(t: any): string {
        const hr = t?._highlightResult?.contactData;
        const cd = t?.contactData;
        return this.hl(hr?.companyName, cd?.companyName) || this.hl(hr?.email, cd?.email) || '';
    }

    trackByObjectID(index: number, item: any): any {
        return item?.objectID || item?.id || index;
    }

    trackBySectionType(index: number, section: { type: string }): string {
        return section.type;
    }

    /**
     * Ordered result sections. The list that VISIBLY highlights the query (a
     * match in a displayed field) is shown first; ties (both or neither
     * highlighted) keep "Mieter" first. Nothing is dropped. Empty sections are
     * removed.
     */
    get orderedSections(): Array<{ label: string; type: 'property' | 'tenant'; hits: any[] }> {
        const properties = {label: 'Immobilien', type: 'property' as const, hits: this.propertyHits || []};
        const tenants = {label: 'Mieter', type: 'tenant' as const, hits: this.tenantHits || []};
        const tenantsHighlighted = (this.tenantHits || []).some(t => this.tenantHasVisibleMatch(t));
        const propertiesHighlighted = (this.propertyHits || []).some(p => this.propertyHasVisibleMatch(p));
        const propertiesFirst = propertiesHighlighted && !tenantsHighlighted;
        const ordered = propertiesFirst ? [properties, tenants] : [tenants, properties];
        return ordered.filter(s => s.hits.length > 0);
    }

    titleHtml(section: { type: string }, hit: any): string {
        return section.type === 'property' ? this.propertyTitleHtml(hit) : this.tenantTitleHtml(hit);
    }

    subtitleHtml(section: { type: string }, hit: any): string {
        return section.type === 'property' ? this.propertySubtitleHtml(hit) : this.tenantSubtitleHtml(hit);
    }

    // True if any leaf of an Algolia _highlightResult node actually matched
    // (matchLevel !== 'none'). Recurses into nested objects/arrays.
    private anyMatch(node: any): boolean {
        if (!node) {
            return false;
        }
        if (typeof node.matchLevel === 'string') {
            return node.matchLevel !== 'none';
        }
        if (typeof node === 'object') {
            return Object.values(node).some(v => this.anyMatch(v));
        }
        return false;
    }

    // Keep a property result only when the query matched a displayed field
    // (name, h_id or address) — not a hidden field like an embedded landlord
    // name (searchableAttributes is unset, so all fields match).
    private propertyHasVisibleMatch(p: any): boolean {
        const hr = p?._highlightResult;
        if (!hr) {
            return true;
        }
        return this.anyMatch(hr.name) || this.anyMatch(hr.h_id_text) || this.anyMatch(hr.h_id) || this.anyMatch(hr.address);
    }

    // Same for tenants: keep only those matching a displayed field
    // (name / company / email), not a hidden one like their unit's address.
    private tenantHasVisibleMatch(t: any): boolean {
        const hr = t?._highlightResult?.contactData;
        if (!hr) {
            return true;
        }
        return this.anyMatch(hr.displayName)
            || this.anyMatch(hr.firstName)
            || this.anyMatch(hr.lastName)
            || this.anyMatch(hr.companyName)
            || this.anyMatch(hr.email);
    }

    private runSearch(query: string): void {
        // getAlgoliaSearchClient(false) short-circuits empty queries to no request.
        const client: any = this.authService.getAlgoliaSearchClient(false);
        if (!this.authService.algoliaClient || !client) {
            return;
        }

        const customerID = this.authService.customerID;
        const filters = environment.pool && customerID ? `customerID:${customerID}` : undefined;
        const params: any = {query, hitsPerPage: 5, highlightPreTag: '<mark>', highlightPostTag: '</mark>'};
        if (filters) {
            params.filters = filters;
        }

        this.loading = true;
        this.cf.markForCheck();

        client.search([
            {indexName: environment.algolia.indexName.properties, params},
            {indexName: environment.algolia.indexName.tenants, params},
        ])
            .then((res: any) => {
                // Keep ALL matches (a property may relate to the term via a
                // linked person, etc.); ordering handles relevance.
                this.propertyHits = res?.results?.[0]?.hits || [];
                this.tenantHits = res?.results?.[1]?.hits || [];
                this.loading = false;
                this.cf.markForCheck();
            })
            .catch((err: any) => {
                console.error('global-search error:', err);
                this.propertyHits = [];
                this.tenantHits = [];
                this.loading = false;
                this.cf.markForCheck();
            });
    }
}
